import { useUser } from "@/app/providers";
import { usePartnersList, useRolesOnlyList, useTenantList } from "@/hooks";
import { useMemo } from "react";

type TenantUser = {
  userId?: string;
  role?: string;
};

type TenantRow = {
  users?: TenantUser[];
};

export const useUserRoles = () => {
  const { user } = useUser();
  const userId = user?.id;
  const rolesFromUser = user?.roles;
  const { rolesList, loading: loadingRolesList } = useRolesOnlyList();
  const tenantListProps = useMemo(() => ({ ignoreRole: true }), []);
  const { tenantsList, loading: loadingTenantsList } =
    useTenantList(tenantListProps);
  const partnersListProps = useMemo(
    () => ({ email: user?.email }),
    [user?.email],
  );
  const { partnerList, loading: loadingPartners } =
    usePartnersList(partnersListProps);

  const userRoles = useMemo(() => {
    // Wait for all data to be loaded
    if (loadingRolesList || loadingTenantsList || loadingPartners) {
      return [];
    }

    const rolesMap = new Map();

    // Add roles from user.roles array
    if (rolesFromUser?.length) {
      rolesFromUser.forEach((roleKey: string) => {
        let rowObject;
        if (roleKey === "super-admin") {
          rowObject = {
            __typename: "GGRolesType",
            id: roleKey,
            key: roleKey,
            label: "Super Admin",
            role: "Super Admin",
            value: "Super Admin",
          };
        } else {
          rowObject = rolesList.find((obj) => obj.key === roleKey);
        }

        if (rowObject && rowObject.key) {
          rolesMap.set(rowObject.key, rowObject);
        }
      });
    }

    // Add roles from tenants
    if (tenantsList?.length) {
      tenantsList.forEach((tenantRow: TenantRow) => {
        const tenantUser = tenantRow.users?.find(
          (user: TenantUser) => user.userId === userId,
        );
        if (tenantUser?.role) {
          const rowObject = rolesList.find(
            (obj) => obj.key === tenantUser.role,
          );
          if (rowObject && rowObject.key) {
            rolesMap.set(rowObject.key, rowObject);
          }
        }
      });
    }

    // Add roles from partners
    if (partnerList?.length) {
      partnerList.forEach((partnerRow) => {
        const partnerUser = partnerRow.users?.find(
          (user) => user.userId === userId,
        );
        if (partnerUser?.role) {
          const rowObject = rolesList.find(
            (obj) => obj.key === partnerUser.role,
          );
          if (rowObject && rowObject.key) {
            rolesMap.set(rowObject.key, rowObject);
          }
        }
      });
    }

    // Convert Map to array
    return Array.from(rolesMap.values());
  }, [
    rolesFromUser,
    rolesList,
    tenantsList,
    partnerList,
    userId,
    loadingRolesList,
    loadingTenantsList,
    loadingPartners,
  ]);

  return {
    userRoles,
    loading: loadingRolesList || loadingTenantsList || loadingPartners,
  };
};
